/**
 * Profile · who this learner is and what they have done, at `?profile=me`.
 *
 * Two things the product had nowhere to put. The first is the record itself:
 * the level list shows XP, levels and stars because those are what a map needs,
 * and everything else it knows — the levels cleared without a single hint, the
 * hints taken, the attempts, the clears the grader could not vouch for, the
 * chapter somebody stalled in — had no screen. It is worth a screen. A course
 * that only ever shows you what is next is a treadmill; a record you can look
 * back at is the reason to have done it.
 *
 * The second is the operator. Everyone in a chat room here is a figure beside
 * a name, and until now that figure was an emoji chosen from a list of
 * fifty-six. The avatar system is the product's own: nine rails of parts drawn
 * 16×16 off the brand ramp, no photograph, no upload, nothing to moderate
 * (lib/avatar.ts). Building one is the one act of self-description this course
 * asks for, and it belongs next to the record because they are the same
 * sentence — this is who I am here, and this is what I have done.
 *
 * ## Why it is a screen and not a dialog
 *
 * It has a URL, it is long, and it is somewhere a person goes deliberately. A
 * dialog over the level list would make the record feel like a setting. It
 * takes the same shape as the debrief — a bar, a column, a way back — because
 * this product has exactly two kinds of screen, the instrument panel and the
 * page you read, and this is the second kind.
 *
 * ## What it needs from elsewhere
 *
 * Nothing it cannot ask for itself. The record is the tab's own (progress.ts),
 * the account is the shell's, and the identity — the name and the tag a room
 * shows — comes from the chat preferences and the protocol's own hash rather
 * than from a socket: this screen must not open one, and a tag is a pure
 * function of an id.
 */
import { useCallback, useEffect, useMemo, useState, useSyncExternalStore, type FormEvent } from 'react';
import { LIMITS, accountName, accountTag, cleanName, tagFor } from '@engineer-os/chat';
import type { ProfileProps } from '../contracts';
import { levelProgress, rankFor, totals } from '../../progress';
import { chatPrefs } from '../../lib/chat';
import { personPath } from '../../lib/person';
import { openRoom } from '../../lib/dock';
import { hereName, onName, rename } from '../../lib/naming';
import { derived as derivedSeed, onOperator, wear, worn } from '../../lib/operator';
import { toSeed, parseSeed } from '../../lib/avatar';
import { num, shortDate } from '../../lib/format';
import { useLocale, useStrings } from '../../state/useLocale';
import { useTeams } from '../../state/useTeams';
import { sound } from '../../lib/sound';
import { Brand, Button, LinkButton, Machine, Meter, Parts, Tag } from '../../ui';
import { Builder } from '../avatar/Builder';
import { Operator, OperatorPlate } from '../avatar/Operator';
import { AvatarRewards } from '../avatar/Rewards';
import { avatarWords } from '../../lib/strings/avatar-rewards';
import { COURSE_HREF } from '../../lib/landing';


export function Profile({ missions, progress, account }: ProfileProps) {
  const words = useStrings();
  const s = words.profile;
  const { locale } = useLocale();

  /* ── Who ──────────────────────────────────────────────────────────────── */

  const prefs = chatPrefs();
  // Subscribed for the same reason the figure below is: this screen is one of
  // the two places a signed-out learner's name is set, and the other is the
  // chat panel at the foot of it (lib/naming.ts). A signed-in name comes down
  // with the account, which `rename` updates in place (state/useAccount.ts).
  const picked = useSyncExternalStore(onName, hereName, hereName);
  const name = account.user ? accountName(account.user.name) : picked;
  const id = account.user?.id ?? prefs.id;

  // The tag is a hash, so it is asked for rather than computed inline, and it
  // is allowed to be absent for a frame: nothing on this screen depends on it.
  const [tag, setTag] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    const want = account.user ? accountTag(account.user.id) : tagFor(prefs.id);
    void want.then((t) => {
      if (live) setTag(t);
    });
    return () => {
      live = false;
    };
  }, [account.user, prefs.id]);

  /* ── The figure ───────────────────────────────────────────────────────── */

  // Subscribed rather than read once, because this screen is not the only
  // thing that writes it: the account's own figure lands here a moment after
  // the page does, when /api/auth/me comes back and `syncOperator` adopts it
  // (lib/operator.ts). A read at mount would draw the derived figure and then
  // sit on it while the room beside it showed the real one.
  const builtSeed = useSyncExternalStore(onOperator, worn, worn);
  const base = useMemo(() => derivedSeed(id), [id]);
  const seed = builtSeed ?? base;
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<'saved' | 'failed' | null>(null);
  const [copied, setCopied] = useState(false);

  /* ── The name ──────────────────────────────────────────────────────────
     Two stores behind one form. Signed in, the name is a column on the
     account and changing it is a round trip that can be refused; signed out,
     it is this browser's and cannot fail. The form does not say which — the
     sentence under it does. */
  const [renaming, setRenaming] = useState(false);
  const [typed, setTyped] = useState('');
  const [nameBusy, setNameBusy] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [nameSaid, setNameSaid] = useState<'saved' | 'failed' | null>(null);

  /* ── The page other people can open ───────────────────────────────────── */

  /**
   * The absolute address of this learner's public page, once there is one.
   *
   * Absolute because it is meant to be copied and pasted somewhere this
   * product is not, and a relative path in a message is not a link. Guarded on
   * `window` because build/shot renders this component to static markup in
   * Node to photograph it, and a screen that threw there would cost a
   * developer the one tool that shows them what they changed.
   *
   * Signed in only. A local-only learner has no account, so there is no row
   * mapping a tag back to anybody and the page would answer 404 — a link the
   * product offered and the product refuses.
   */
  const publicUrl = useMemo(() => {
    if (!account.user || !tag || typeof window === 'undefined') return null;
    return new URL(personPath(tag), window.location.origin).href;
  }, [account.user, tag]);

  const t = totals(progress, missions);
  const cleared = t.cleared;

  /* ── Who with ───────────────────────────────────────────────────────────
     The same store the chat panel reads (lib/teams.ts). Two copies of this
     state would mean leaving a team in the panel and watching the page behind
     it go on showing it — and on this screen the panel is literally on top. */
  const teams = useTeams(account.user !== null);

  const save = useCallback(
    (next: string | null) => {
      setBusy(true);
      setSaid(null);
      // Nothing to set here afterwards: `wear` announces, and the
      // subscription above is what redraws the figure at the top of the page
      // — and the same announcement is what moves it in every room this tab
      // has open (state/useChat.ts).
      void wear(next, account.user !== null).then((ok) => {
        setBusy(false);
        setEditing(false);
        setSaid(ok ? 'saved' : 'failed');
      });
    },
    [account.user],
  );

  /**
   * Save a typed name, or put the account's own back when `raw` is null.
   *
   * The rule is checked here before anything is sent, with the protocol's own
   * `cleanName` and the protocol's own sentence in this reader's language, so
   * the form never promises what the server then refuses — the same
   * arrangement the join prompt has (components/chat/NamePrompt.tsx).
   *
   * Nothing here is optimistic, and that is the difference from the builder
   * above. A figure is written to this browser and announced before the
   * account hears about it, because it is a direct manipulation of something
   * on screen; a name is what other people call you, so what goes on screen is
   * what the account came back holding. A rename that did not land leaves the
   * form open with the old name still in the heading.
   */
  const saveName = useCallback(
    async (raw: string | null) => {
      setNameSaid(null);
      setNameError(null);
      const next = raw === null ? null : cleanName(raw);
      if (next === null && raw !== null) {
        setNameError(words.chat.rules.name());
        return;
      }
      if (!account.user) {
        // This browser's own, and there is nothing that can fail: the store
        // writes the preferences and tells the open sockets (lib/naming.ts).
        rename(next);
        setRenaming(false);
        setNameSaid('saved');
        return;
      }
      setNameBusy(true);
      const got = await account.rename(next);
      setNameBusy(false);
      if (got === null) {
        setNameSaid('failed');
        return;
      }
      setRenaming(false);
      setNameSaid('saved');
    },
    [account, words],
  );

  /* ── Closing the account ───────────────────────────────────────────────
     Five pieces of state for one button, and each of them is a different
     question the screen has to be able to answer: is the confirmation open,
     what has been typed into it, has the server asked for the forfeit press,
     is a request in flight, and did the last one fail. Collapsing any two of
     them would make a state the screen cannot draw — a failed delete with the
     confirmation closed, most obviously, which is a learner told nothing.

     `gone` is the sixth and is the odd one. The account becomes null the
     moment the delete lands, so without it this block would redraw as the
     signed-out sentence — "there is no account to delete" — half a second
     after somebody deleted one, which reads as though nothing happened. */
  const [closing, setClosing] = useState(false);
  const [phrase, setPhrase] = useState('');
  const [forfeit, setForfeit] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteFailed, setDeleteFailed] = useState(false);
  const [gone, setGone] = useState(false);
  // Case-folded, because the word is a confirmation and not a password: a
  // learner who typed it with a capital meant it exactly as much.
  const typedOk = phrase.trim().toLocaleLowerCase(locale) === s.deletion.phrase.toLocaleLowerCase(locale);

  /**
   * Ask for the account to be deleted, once the word has been typed.
   *
   * `'entitled'` is not a failure and is the reason this is not a boolean: the
   * server has refused a delete that did not acknowledge a purchase, so the
   * screen shows the sentence saying so and the same button asks again. The
   * second press carries the acknowledgement. Nothing here decides whether
   * there is something to forfeit — the tab does not know, and a tab that
   * guessed would either warn people who bought nothing or fail to warn the
   * one person it matters to (worker/account.ts, `deleteAccount`).
   */
  const confirmDelete = useCallback(async () => {
    setDeleting(true);
    setDeleteFailed(false);
    const outcome = await account.deleteAccount(forfeit);
    setDeleting(false);
    if (outcome === 'gone') {
      setClosing(false);
      setGone(true);
      return;
    }
    if (outcome === 'entitled') {
      setForfeit(true);
      return;
    }
    setDeleteFailed(true);
  }, [account, forfeit]);

  /* ── The record ───────────────────────────────────────────────────────── */

  const { current: rank, next: nextRank, pct } = rankFor(progress.xp);
  const record = useMemo(() => {
    let perfect = 0;
    let noHints = 0;
    let hints = 0;
    let attempts = 0;
    let skipped = 0;
    let first: number | null = null;
    let latest: number | null = null;
    for (const m of missions) {
      const lp = levelProgress(progress, m.id);
      attempts += lp.attempts;
      if (lp.skipped && !lp.completed) skipped++;
      if (!lp.completed) continue;
      hints += lp.hintsUsed;
      if (lp.stars === 3) perfect++;
      if (lp.hintsUsed === 0) noHints++;
      if (lp.completedAt !== null) {
        first = first === null ? lp.completedAt : Math.min(first, lp.completedAt);
        latest = latest === null ? lp.completedAt : Math.max(latest, lp.completedAt);
      }
    }
    return { perfect, noHints, hints, attempts, skipped, first, latest };
  }, [missions, progress]);

  /* Chapters, derived the way the level list derives them: the levels of one
     track that share a `world`, in course order, so a chapter added to the
     course turns up here without this file being touched.

     It was adjacency until tracks arrived — a row went on collecting levels
     for as long as the next one carried the same `world` string — and that
     said the same thing for as long as the course was a single line of
     levels. It stops saying it with two: every track numbers itself from 0
     (docs/curriculum.md §5) and they arrive here one path after another, so a
     chapter name both tracks use would be cut in half by wherever the first
     track ended, and this table would show one heading twice with a learner's
     levels divided between the rows. A chapter is the pair (track, world) —
     the same name on two paths is two chapters, counted apart, because they
     are two different sets of levels done by two different people. */
  const worlds = useMemo(() => {
    interface WorldRow {
      /* The pair, for the row's React key: the name alone stopped being unique. */
      id: string;
      name: string;
      cleared: number;
      total: number;
      stars: number;
      reached: boolean;
    }
    const rows = new Map<string, WorldRow>();
    const out: WorldRow[] = [];
    for (const m of missions) {
      const lp = levelProgress(progress, m.id);
      const id = `${m.track}#${m.world}`;
      let row = rows.get(id);
      if (!row) {
        row = { id, name: m.world, cleared: 0, total: 0, stars: 0, reached: false };
        rows.set(id, row);
        out.push(row);
      }
      row.total++;
      row.stars += lp.stars;
      if (lp.completed) row.cleared++;
      if (lp.completed || lp.skipped || lp.attempts > 0) row.reached = true;
    }
    return out;
  }, [missions, progress]);

  const date = (at: number | null) => (at === null ? '—' : shortDate(new Date(at), locale));

  return (
    <div className="profile">
      <header className="profile-top">
        <Brand compact />
        <nav className="profile-top-nav">
          <LinkButton variant="quiet" href={COURSE_HREF}>
            {s.back}
          </LinkButton>
        </nav>
      </header>

      <main className="profile-page">
        {/* ── The person ───────────────────────────────────────────────── */}
        <section className="profile-who">
          <OperatorPlate seed={seed} size={128} alt={s.operator.label} />
          <div className="profile-who-copy">
            <p className="label">{s.operator.label}</p>
            <div className="profile-name-row">
              <h1 className="profile-name" dir="auto">
                {name ?? s.noName}
              </h1>
              <Button
                variant="quiet"
                onClick={() => {
                  sound.click();
                  if (renaming) {
                    setRenaming(false);
                    return;
                  }
                  // Seeded with what is on screen, so the common change is an
                  // edit rather than a retype. A learner who has never picked
                  // one starts empty rather than with the word "Learner",
                  // which is what the screen says about them and not a name
                  // they chose.
                  setTyped(name ?? '');
                  setNameError(null);
                  setNameSaid(null);
                  setRenaming(true);
                }}
              >
                {renaming ? s.name.close : s.name.edit}
              </Button>
            </div>
            {renaming && (
              <form
                className="profile-rename"
                onSubmit={(e: FormEvent) => {
                  e.preventDefault();
                  void saveName(typed);
                }}
              >
                <label className="label" htmlFor="profile-name-input">
                  {s.name.field}
                </label>
                <input
                  id="profile-name-input"
                  className="profile-rename-input"
                  type="text"
                  value={typed}
                  maxLength={LIMITS.name.max}
                  autoComplete="off"
                  spellCheck={false}
                  autoFocus
                  // A name may be written in either script, so the field
                  // follows what is typed into it rather than the direction of
                  // the page (docs/i18n.md).
                  dir="auto"
                  aria-describedby="profile-name-note"
                  aria-invalid={nameError !== null}
                  onChange={(e) => {
                    setTyped(e.target.value);
                    if (nameError) setNameError(null);
                  }}
                />
                <p id="profile-name-note" className="note" role={nameError ? 'alert' : undefined}>
                  {nameError ?? s.name.note}
                </p>
                <p className="profile-rename-actions">
                  <Button type="submit" variant="solid" disabled={nameBusy}>
                    {nameBusy ? s.name.saving : s.name.save}
                  </Button>
                  {/* Offered to anybody signed in with Google, whether or not
                      they have ever chosen a name: Google's is underneath
                      either way, so this is the way back rather than a state
                      the screen has to know about. An account opened with an
                      emailed code has nothing underneath but "Learner", and a
                      button that goes back to that is not a way back. */}
                  {account.user?.google && (
                    <Button
                      type="button"
                      variant="quiet"
                      disabled={nameBusy}
                      onClick={() => {
                        sound.click();
                        void saveName(null);
                      }}
                    >
                      {s.name.google}
                    </Button>
                  )}
                </p>
                <p className="note">
                  {account.user ? (account.user.google ? s.name.googleWhy : s.name.keptWhy) : s.name.localOnly}
                </p>
              </form>
            )}
            {nameSaid && (
              <p className={nameSaid === 'failed' ? 'note profile-said is-bad' : 'note profile-said'}>
                {nameSaid === 'saved' ? s.name.saved : s.name.failed}
              </p>
            )}
            <p className="profile-marks">
              {tag && <Machine className="profile-tag">#{tag}</Machine>}
              <Tag tone="on">{rank.name}</Tag>
              {!account.user && <Tag>{s.localOnly}</Tag>}
            </p>
            <p className="note">
              {s.operator.what} {s.operator.where}
            </p>
            <p className="profile-seed">
              <span className="label">{s.operator.seed}</span>
              <Machine>{toSeed(parseSeed(seed))}</Machine>
              <span className="note">{builtSeed ? s.operator.seedWhy : s.operator.derivedWhy}</span>
            </p>
            <p className="profile-who-actions">
              <Button
                variant={editing ? 'quiet' : 'solid'}
                onClick={() => {
                  sound.click();
                  setEditing((e) => !e);
                  setSaid(null);
                }}
              >
                {editing ? s.operator.close : s.operator.edit}
              </Button>
              {account.enabled && !account.user && (
                <Button variant="quiet" disabled={account.busy} onClick={() => account.signIn()}>
                  {account.busy ? s.signingIn : s.signIn}
                </Button>
              )}
            </p>
            {/* Said where the figure is, because it is about the figure: a
                signed-in learner with a Google picture is wearing it in every
                room until they build one. */}
            {account.user?.picture && !builtSeed && (
              <p className="profile-picture-note">
                <img className="account-avatar" src={account.user.picture} alt="" width={22} height={22} referrerPolicy="no-referrer" />
                <span className="note">
                  {s.operator.picture} · {s.operator.pictureWhy}
                </span>
              </p>
            )}
            {/* --- The page other people can open ---------------------------
                Under the actions rather than beside them: it is not something
                to do to this screen, it is a fact about where this record can
                be read from, and the sentence under it is the disclosure. */}
            <div className="profile-public">
              <p className="label">{s.publicPage.label}</p>
              {publicUrl === null ? (
                /* No account is a sentence. A signed-in learner whose tag has
                   not been hashed yet is a frame, and a frame is not worth a
                   line of copy that would flash and leave. */
                !account.user && <p className="note">{s.publicPage.localOnly}</p>
              ) : (
                <>
                  {/* The address itself, machine text, selectable. A learner
                      who does not trust a copy button should be able to read
                      the link and type it. */}
                  <Machine className="profile-public-url">{publicUrl}</Machine>
                  <p className="profile-who-actions">
                    {/* A new tab, because this screen is mounted over a
                        running virtual machine and a same-tab navigation would
                        throw it away. */}
                    <LinkButton variant="quiet" href={publicUrl} target="_blank" rel="noreferrer">
                      {s.publicPage.open}
                    </LinkButton>
                    <Button
                      variant="quiet"
                      onClick={() => {
                        sound.click();
                        // Clipboard access is refused in an insecure context
                        // and by some browsers without a gesture they
                        // recognise. Nothing is claimed unless it worked: the
                        // address is on screen above this button either way.
                        void navigator.clipboard
                          ?.writeText(publicUrl)
                          .then(() => setCopied(true))
                          .catch(() => setCopied(false));
                      }}
                    >
                      {copied ? s.publicPage.copied : s.publicPage.copy}
                    </Button>
                  </p>
                </>
              )}
              <p className="note">{s.publicPage.what}</p>
            </div>

            {!account.user && <p className="note">{s.localOnlyWhy}</p>}
            {said && <p className={said === 'failed' ? 'note profile-said is-bad' : 'note profile-said'}>{said === 'saved' ? s.build.saved : s.build.failed}</p>}
          </div>
        </section>

        {!editing && <section className="profile-character" aria-label={avatarWords[locale].title}>
          <AvatarRewards level={cleared} selection={parseSeed(seed)} />
          <Button variant="solid" onClick={() => { sound.click(); setEditing(true); }}>{avatarWords[locale].customize}</Button>
        </section>}

        {editing && (
          <section className="profile-build" aria-label={s.build.title}>
            <h2 className="profile-h2">{s.build.title}</h2>
            <Builder seed={seed} derived={base} level={cleared} busy={busy} onSave={save} onCancel={() => setEditing(false)} />
          </section>
        )}

        {/* ── The record ───────────────────────────────────────────────── */}
        <section className="profile-record" aria-label={s.stats.label}>
          <h2 className="profile-h2">{s.stats.label}</h2>
          <div className="profile-rank">
            <div className="profile-rank-name">
              <span className="label">{s.stats.rank}</span>
              <strong className="rank">{rank.name}</strong>
              <span className="note">{rank.blurb}</span>
            </div>
            <div className="profile-rank-meter">
              <Meter pct={pct} label={s.stats.rankMeter} />
              <span className="note">{nextRank ? s.stats.toNextRank(nextRank.xp - progress.xp, nextRank.name) : s.stats.topRank}</span>
            </div>
          </div>

          <dl className="profile-figures">
            <Figure label={s.stats.xp} value={progress.xp} />
            <Figure label={s.stats.levels} value={cleared} of={missions.length} />
            <Figure label={s.stats.stars} value={t.stars} of={t.maxStars} />
            <Figure label={s.stats.perfect} value={record.perfect} />
            <Figure label={s.stats.noHints} value={record.noHints} />
            <Figure label={s.stats.hints} value={record.hints} />
            <Figure label={s.stats.attempts} value={record.attempts} />
            <Figure label={s.stats.unverified} value={t.unverified} note={t.unverified > 0 ? s.stats.unverifiedWhy : undefined} />
            <Figure label={s.stats.skipped} value={record.skipped} note={record.skipped > 0 ? s.stats.skippedWhy : undefined} />
          </dl>

          {cleared === 0 ? (
            <p className="profile-empty">
              <span className="note">{s.stats.nothingYet}</span>
              <LinkButton variant="solid" href={missions[0] ? `?mission=${encodeURIComponent(missions[0].id)}` : '?'}>
                {s.stats.start}
              </LinkButton>
            </p>
          ) : (
            <p className="profile-dates">
              <span className="note">
                {s.stats.started}: <Machine>{date(record.first)}</Machine>
              </span>
              <span className="note">
                {s.stats.latest}: <Machine>{date(record.latest)}</Machine>
              </span>
            </p>
          )}
        </section>


        {/* ── Who with ─────────────────────────────────────────────────
            After the record and before the chapters. A record is what somebody
            did; a team is who they are doing it with, and it belongs beside the
            first rather than filed under the course.

            Read-only, deliberately. Making a team, inviting somebody, leaving
            and answering an invitation are all writes, and they all already
            work in the chat panel with their refusals already worded. What was
            missing was not a second place to do them — it was any sign on this
            screen that teams exist at all. */}
        <section className="profile-teams" aria-label={s.teams.label}>
          <h2 className="profile-h2">{s.teams.label}</h2>
          {!account.user ? (
            <p className="note">{s.teams.localOnly}</p>
          ) : teams.teams.length === 0 ? (
            <>
              {/* Loading and empty are the same shape and different sentences.
                  A learner in no teams is the ordinary case on this screen. */}
              <p className="note">{teams.loading ? words.chat.teams.loading : s.teams.none}</p>
              {!teams.loading && <p className="note">{s.teams.noneWhy}</p>}
            </>
          ) : (
            <ul className="profile-team-list">
              {teams.teams.map((t) => (
                <li key={t.id} className="profile-team">
                  <div className="profile-team-head">
                    {/* A team is called what its members called it, and that
                        may be in either script (docs/i18n.md §7). */}
                    <strong className="profile-team-name" dir="auto">
                      {t.name}
                    </strong>
                    {/* The panel's own words for the same two facts, read
                        across domains on purpose: two rosters of the same
                        people should not describe them differently. */}
                    <span className="note">{words.chat.teams.size(t.members.length)}</span>
                    {t.role === 'lead' && <Tag>{words.chat.teams.lead}</Tag>}
                  </div>
                  <ul className="profile-team-roster">
                    {t.members.map((m) => (
                      <li key={m.tag ?? m.name} className="profile-team-member">
                        {/* The figure a seed draws, at the list size the chat
                            uses. No level and no rank: this is who is in the
                            room, not a scoreboard (chat/Teams.tsx). */}
                        {m.avatarSeed ? <Operator seed={m.avatarSeed} size={16} /> : <span className="profile-team-blank" aria-hidden="true" />}
                        <span dir="auto">{m.name}</span>
                        {m.role === 'lead' && <span className="note">{words.chat.teams.lead}</span>}
                      </li>
                    ))}
                  </ul>
                  {/* The one press on this block, and it is a way in rather
                      than a change: a team's room is the only place its roster
                      can actually talk. It opens the dock over this screen
                      instead of navigating, because the dock is a panel and
                      not a place (lib/dock.ts). */}
                  <p className="profile-team-actions">
                    <Button
                      variant="quiet"
                      onClick={() => {
                        sound.click();
                        openRoom(t.room);
                      }}
                    >
                      {s.teams.open}
                    </Button>
                  </p>
                </li>
              ))}
            </ul>
          )}
          {/* Under the list, whether or not there is one: being asked into your
              first team is exactly the case where there is nothing above this. */}
          {teams.invites.length > 0 && <p className="note profile-team-waiting">{s.teams.waiting}</p>}
        </section>

        {/* ── By chapter ───────────────────────────────────────────────── */}
        <section className="profile-worlds" aria-label={s.worlds.label}>
          <h2 className="profile-h2">{s.worlds.label}</h2>
          <table className="profile-table">
            <thead>
              <tr>
                <th scope="col">{s.worlds.chapter}</th>
                <th scope="col">{s.worlds.cleared}</th>
                <th scope="col">{s.worlds.stars}</th>
              </tr>
            </thead>
            <tbody>
              {worlds.map((w) => (
                <tr key={w.id} className={w.cleared === w.total ? 'is-done' : undefined}>
                  {/* The `world` string is the English key from the mission
                      files and stays that way; the catalogue is where it
                      becomes a name (docs/i18n.md §6.1). */}
                  <th scope="row">{words.home.worlds[w.name]?.name ?? w.name}</th>
                  <td>
                    <Machine>{num(w.cleared) + '/' + num(w.total)}</Machine>
                  </td>
                  <td>{w.reached ? <Machine>{num(w.stars) + '/' + num(w.total * 3)}</Machine> : <span className="note">{s.worlds.locked}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {/* ── Closing the account ──────────────────────────────────────
            Last on the page, under everything it destroys. A learner reaches
            this having just scrolled past the record, the teams and the
            chapters, which is the only place a confirmation of this size can
            honestly sit: the screen has already shown them what is at stake
            by the time it offers to take it away.

            There is no dialog and no takeover. The two irreversible acts this
            product already has are a message deletion, which takes the chat
            panel over because a 280px panel cannot show a message and a
            confirmation side by side (chat/DeleteDialog.tsx), and a workspace
            reset, which is a browser confirm because it costs a few minutes.
            This page is neither: it is wide, it is a reading screen, and the
            thing the confirmation has to quote is a list. So the list goes
            here, in the column, where it can simply be read. */}
        <section aria-label={s.deletion.label}>
          <h2 className="profile-h2">{s.deletion.label}</h2>
          {gone ? (
            <p className="note">{s.deletion.toastGone}</p>
          ) : !account.user ? (
            <p className="note">{s.deletion.localOnly}</p>
          ) : !closing ? (
            <>
              <p className="note">{s.deletion.what}</p>
              <p className="profile-who-actions">
                {/* Quiet, and the only quiet-to-solid escalation on this page.
                    Opening the confirmation does nothing, so the press that
                    does nothing is the unremarkable one and the press that
                    deletes is the loud one below. */}
                <Button
                  variant="quiet"
                  onClick={() => {
                    sound.click();
                    setPhrase('');
                    setForfeit(false);
                    setDeleteFailed(false);
                    setClosing(true);
                  }}
                >
                  {s.deletion.open}
                </Button>
              </p>
            </>
          ) : (
            /* Deliberately not a form. A form would delete the account on
               Enter, and the one keystroke a learner is most likely to press
               without deciding to is the one at the end of typing a word. */
            <div className="profile-rename">
              <p className="label">{s.deletion.title}</p>

              <div className="prose">
                <p className="label">{s.deletion.goesLabel}</p>
                <ul>
                  {s.deletion.goes.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
                <p className="label">{s.deletion.staysLabel}</p>
                <ul>
                  {s.deletion.stays.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </div>

              <p className="note">{s.deletion.again}</p>

              {/* Shown only after the server has said there is something to
                  forfeit, so a learner who bought nothing never reads a
                  sentence about a refund they were never owed. */}
              {forfeit && <p className="note profile-said is-bad">{s.deletion.entitled}</p>}

              <label className="label" htmlFor="profile-delete-input">
                {s.deletion.field(s.deletion.phrase)}
              </label>
              <input
                id="profile-delete-input"
                className="profile-rename-input"
                type="text"
                value={phrase}
                autoComplete="off"
                spellCheck={false}
                autoFocus
                /* The word is Arabic in an Arabic catalogue and Latin in the
                   English one, so the field follows what is typed rather than
                   the direction of the page (docs/i18n.md). */
                dir="auto"
                onChange={(e) => setPhrase(e.target.value)}
              />

              <p className="profile-rename-actions">
                {/* Solid for DeleteDialog's reason: this screen's whole subject
                    is this button, and a confirmation whose own action is the
                    quiet one reads as a mistake. The safeguards are that the
                    screen exists, that a word has to be typed, and that the
                    server refuses a request that does not name this account. */}
                <Button type="button" variant="solid" disabled={!typedOk || deleting} onClick={() => void confirmDelete()}>
                  {deleting ? s.deletion.deleting : forfeit ? s.deletion.entitledConfirm : s.deletion.confirm}
                </Button>
                <Button
                  type="button"
                  variant="quiet"
                  disabled={deleting}
                  onClick={() => {
                    sound.click();
                    setClosing(false);
                  }}
                >
                  {s.deletion.cancel}
                </Button>
              </p>

              {deleteFailed && <p className="note profile-said is-bad">{s.deletion.failed}</p>}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

/**
 * One figure in the record: a number, its label, and the total it is out of.
 *
 * "7/52" is one reading and the catalogue decides where the total sits, so it
 * is assembled with `Parts` from the same sentence the level list uses rather
 * than joined here with a slash (lib/strings/home.ts, `outOf`).
 */
function Figure({ label, value, of, note }: { label: string; value: number; of?: number; note?: string }) {
  const s = useStrings();
  return (
    <div className="figure">
      <dt>{label}</dt>
      <dd>{of === undefined ? <em>{num(value)}</em> : <Parts of={s.home.standing.outOf(<em>{num(value)}</em>, of)} />}</dd>
      {note && <p className="note figure-note">{note}</p>}
    </div>
  );
}

export default Profile;
